import { Button, IconButton } from '@mui/material';
import { CollapseIcon } from 'assets/icons';
import clsx from 'clsx';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

function filteredRawConfig<T extends object>(config: T): T {
  return {
    ...config,
    name: undefined,
    created: undefined,
    created_by: undefined,
    status: undefined,
    valid_from_timestamp: undefined,
  };
}

interface OptimizationAdminPanelProps {
  json: string;
  updateValues: (parameters: string) => void;
}

export function OptimizationAdminPanel({
  json,
  updateValues,
}: Readonly<OptimizationAdminPanelProps>): ReactElement<OptimizationAdminPanelProps> {
  const [t] = useTranslation();
  const [expanded, toggleExpanded] = useToggle(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const formattedJSON = JSON.stringify(filteredRawConfig(JSON.parse(json)), undefined, 2);

  const [editedJSON, setEditedJSON] = useState(formattedJSON);

  const [showError, setShowError] = useState(false);
  const resetJSON = () => {
    setShowError(false);
    setEditedJSON(formattedJSON);
  };
  const saveJSON = () => {
    try {
      setShowError(false);
      updateValues(JSON.stringify(filteredRawConfig(JSON.parse(editedJSON))));
    } catch (error) {
      setShowError(true);
      console.error(error);
    }
  };

  return (
    <div className="grid grid-cols-1 m-6">
      <div
        className={clsx(
          'flex items-center justify-between flex-wrap min-h-10 p-6 border border-solid border-divider',
          expanded ? 'rounded-t-lg' : 'rounded-lg',
        )}
      >
        <div className="flex items-center gap-x-2">
          <button
            className="appearance-none p-0 border-none bg-transparent text-base font-sans cursor-pointer"
            onClick={toggleExpanded}
          >
            {t('COPT_RAW_CONFIG')}
          </button>
          <IconButton onClick={toggleExpanded}>
            <CollapseIcon className={clsx('size-4', { '-scale-y-100': !expanded })} />
          </IconButton>
        </div>
        {expanded && (
          <div className="flex items-center gap-x-2">
            {showError && <p className="m-0 text-error-500">{t('SHARED_ERROR_INVALID_JSON')}</p>}
            <Button variant="outlined" onClick={resetJSON}>
              {t('SHARED_RESET')}
            </Button>
            <Button onClick={saveJSON}>{t('SHARED_SUBMIT')}</Button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="relative grid grid-cols-1">
          {!warningAcknowledged && (
            <div className="absolute -top-px inset-x-0 p-6 flex flex-col items-end gap-2 flex-wrap text-warning bg-gradient-to-r from-warning/10 to-warning/10 bg-white border-solid border border-warning shadow-sm">
              <div>
                <h2 className="m-0">{t('COPT_RAW_CONFIG_WARNING_TITLE')}</h2>
                <p className="m-0">{t('COPT_RAW_CONFIG_WARNING')}</p>
              </div>
              <Button className="shrink-0 bg-warning text-white" onClick={() => setWarningAcknowledged(true)}>
                {t('SHARED_ACKNOWLEDGED')}
              </Button>
            </div>
          )}
          <textarea
            className="p-6 bg-grey-50 shadow-inner border border-t-0 rounded-b-lg border-solid border-divider focus:outline-none"
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            rows={editedJSON.split(/\r\n|\r|\n/).length}
            value={editedJSON}
            onChange={(event) => setEditedJSON(event.target.value)}
          />
        </div>
      )}
    </div>
  );
}
